<template>
    <div
        v-if="isVisible"
        class="widget-container"
        :class="[`widget-position-${position}`, classes.container, { 'widget-mobile': isMobile, 'widget-dragging': isDragging }]"
        :style="computedContainerStyle"
        @pointerdown="handlePointerDown"
    >
        <!-- Widget Content -->
        <div
            v-if="showContent"
            class="widget-content"
            :class="[classes.content, { 'widget-content-mobile': isMobile }]"
            :style="computedContentStyle"
        >
            <slot></slot>
        </div>

        <!-- Close button - positioned outside content wrapper -->
        <button
            v-if="showContent && showCloseButton"
            type="button"
            class="btn-close-widget"
            :class="classes.closeButton"
            :style="computedCloseButtonStyle"
            @click="handleCloseClick"
        >
            <i class="fa-solid fa-xmark"></i>
        </button>

        <!-- Widget Icon -->
        <div
            v-if="!showContent || showIconToggle"
            class="widget-icon"
            :class="[classes.icon, { 'widget-icon-with-content': showContent && showIconToggle }]"
            :datatest="showContent ? currentIconClass.show : currentIconClass.hide"
            :style="computedIconStyle"
            @click="handleIconClick"
        >
            <!-- Icon for show state (when content is visible) -->
            <span v-show="showContent">
                <i :class="currentIconClass.show"></i>
            </span>
            <!-- Icon for hide state (when content is hidden) -->
            <span v-show="!showContent">
                <i :class="currentIconClass.hide"></i>
            </span>
        </div>
    </div>
</template>

<script>
/**
 * ****************************************************************************
 * @description     :   Common Widget Component
 * @created at      :   2025/08/19
 * @created by      :   DaoNX
 * @package         :   Move
 * @copyright       :   Copyright (c) A.N.S Asia
 * @version         :   1.0.0
 * ****************************************************************************
 */

export default {
    name: 'AnsWidget',
    props: {
        isVisible: {
            type: Boolean,
            default: false
        },
        showContent: {
            type: Boolean,
            default: false
        },
        position: {
            type: String,
            default: 'bottom-right',
            validator: (value) => ['bottom-right', 'bottom-left', 'top-right', 'top-left', 'bottom-center', 'top-center'].includes(value)
        },
        isMobile: {
            type: Boolean,
            default: false
        },
        iconProps: {
            type: Object,
            default: () => ({})
        },
        showCloseButton: {
            type: Boolean,
            default: true
        },
        showIconToggle: {
            type: Boolean,
            default: false
        },
        styles: {
            type: Object,
            default: () => ({
                container: {},
                icon: {},
                content: {},
                closeButton: {}
            })
        },
        classes: {
            type: Object,
            default: () => ({
                container: '',
                icon: '',
                content: '',
                closeButton: ''
            })
        },
        offset: {
            type: Object,
            default: () => ({
                x: 20,
                y: 20
            })
        },
        draggable: {
            type: Boolean,
            default: false
        }
    },
    emits: ['icon-click', 'close'],
    data() {
        return {
            isDragging: false,
            dragOffset: { x: 0, y: 0 },
            currentPosition: { x: 0, y: 0 }
        };
    },
    computed: {
        computedContainerStyle() {
            const baseStyle = {
                position: 'fixed',
                zIndex: 100
            };

            // Position styles - use dragged position if dragging or dragged
            let positionStyle;
            const isDraggedPosition = this.draggable && (this.isDragging || this.currentPosition.x !== 0 || this.currentPosition.y !== 0);

            if (isDraggedPosition) {
                positionStyle = {
                    left: `${this.currentPosition.x}px`,
                    top: `${this.currentPosition.y}px`,
                    transform: 'none',
                    right: 'auto',
                    bottom: 'auto'
                };
            } else {
                positionStyle = this.getPositionStyle();
            }

            // If dragging, filter out positioning properties from styles.container to prevent override
            const containerStyles = isDraggedPosition
                ? Object.fromEntries(
                      Object.entries(this.styles.container || {}).filter(([key]) => !['left', 'right', 'top', 'bottom', 'transform'].includes(key))
                  )
                : this.styles.container;

            return {
                ...baseStyle,
                ...containerStyles,
                ...positionStyle, // Apply position styles last to prevent override
                cursor: this.draggable && !this.showContent ? 'grab' : 'default',
                userSelect: this.isDragging ? 'none' : 'auto'
            };
        },
        computedIconStyle() {
            return {
                ...this.styles.icon
            };
        },
        computedContentStyle() {
            const baseStyle = {};

            if (this.isMobile) {
                baseStyle.width = '95vw';
                baseStyle.maxWidth = '95vw';
            }

            return {
                ...baseStyle,
                ...this.styles.content
            };
        },
        computedCloseButtonStyle() {
            return {
                ...this.styles.closeButton
            };
        },
        currentIconClass() {
            try {
                // Default icons
                const defaultIcons = {
                    show: 'fa-solid fa-xmark',
                    hide: 'fa-solid fa-plus'
                };

                if (!this.iconProps || !this.iconProps.class) {
                    return defaultIcons;
                }

                const iconClass = this.iconProps.class;

                // If it's a string, use for both states
                if (typeof iconClass === 'string') {
                    return {
                        show: iconClass || defaultIcons.show,
                        hide: iconClass || defaultIcons.hide
                    };
                }

                // If it's an object with show/hide properties
                if (typeof iconClass === 'object' && iconClass !== null) {
                    return {
                        show: iconClass.show || defaultIcons.show,
                        hide: iconClass.hide || defaultIcons.hide
                    };
                }

                return defaultIcons;
            } catch (error) {
                console.error('Error in currentIconClass:', error);
                return {
                    show: 'fa-solid fa-xmark',
                    hide: 'fa-solid fa-plus'
                };
            }
        }
    },
    methods: {
        getPositionStyle() {
            const { x, y } = this.offset;

            switch (this.position) {
                case 'bottom-right':
                    return {
                        bottom: `${y}px`,
                        right: `${x}px`
                    };
                case 'bottom-left':
                    return {
                        bottom: `${y}px`,
                        left: `${x}px`
                    };
                case 'top-right':
                    return {
                        top: `${y}px`,
                        right: `${x}px`
                    };
                case 'top-left':
                    return {
                        top: `${y}px`,
                        left: `${x}px`
                    };
                case 'bottom-center':
                    return {
                        bottom: `${y}px`,
                        left: '50%',
                        transform: 'translateX(-50%)'
                    };
                case 'top-center':
                    return {
                        top: `${y}px`,
                        left: '50%',
                        transform: 'translateX(-50%)'
                    };
                default:
                    return {
                        bottom: `${y}px`,
                        right: `${x}px`
                    };
            }
        },
        handleIconClick() {
            this.$emit('icon-click');
        },
        handleClose() {
            this.$emit('close');
        },
        handleCloseClick() {
            this.$emit('close');
        },
        handlePointerDown(event) {
            if (!this.draggable) return;

            // Interactive tags: input, textarea, button, select, a
            const interactiveTags = ['INPUT', 'TEXTAREA', 'BUTTON', 'SELECT', 'A'];
            const target = event.target;

            // If click on interactive tags, skip drag
            if (interactiveTags.includes(target.tagName)) {
                return;
            }

            this.isDragging = true;
            const rect = this.$el.getBoundingClientRect();

            // Calculate offset from pointer to element's top-left corner
            this.dragOffset.x = event.clientX - rect.left;
            this.dragOffset.y = event.clientY - rect.top;

            // Set initial position if not set
            if (this.currentPosition.x === 0 && this.currentPosition.y === 0) {
                this.currentPosition.x = rect.left;
                this.currentPosition.y = rect.top;
            }

            // Add global event listeners
            document.addEventListener('pointermove', this.handlePointerMove);
            document.addEventListener('pointerup', this.handlePointerUp);

            // Prevent default to avoid text selection
            event.preventDefault();
        },
        handlePointerMove(event) {
            if (!this.isDragging) return;

            // Calculate new position
            const newX = event.clientX - this.dragOffset.x;
            const newY = event.clientY - this.dragOffset.y;

            // Get viewport bounds
            const viewportWidth = window.innerWidth;
            const viewportHeight = window.innerHeight;
            const elementWidth = this.$el.offsetWidth;
            const elementHeight = this.$el.offsetHeight;

            // Constrain to viewport bounds
            this.currentPosition.x = Math.max(0, Math.min(newX, viewportWidth - elementWidth));
            this.currentPosition.y = Math.max(0, Math.min(newY, viewportHeight - elementHeight));
        },
        handlePointerUp() {
            if (!this.isDragging) return;

            this.isDragging = false;

            // Remove global event listeners
            document.removeEventListener('pointermove', this.handlePointerMove);
            document.removeEventListener('pointerup', this.handlePointerUp);
        }
    },
    beforeUnmount() {
        // Clean up event listeners if component is destroyed while dragging
        if (this.isDragging) {
            document.removeEventListener('pointermove', this.handlePointerMove);
            document.removeEventListener('pointerup', this.handlePointerUp);
        }
    }
};
</script>

<style lang="scss" scoped>
.widget-container {
    // Animation for bubble effect
    animation: fadeInBubble 0.3s cubic-bezier(0.25, 0.46, 0.45, 0.94);
    position: relative; // Ensure close button can be positioned relative to this

    &.widget-mobile {
        .widget-content {
            max-height: 80vh;
            overflow-y: auto;
        }
    }

    &.widget-dragging {
        cursor: grabbing !important;
        user-select: none;
        z-index: 1000; // Ensure dragged element is on top

        .widget-icon {
            cursor: grabbing !important;
        }
    }
}

.widget-icon {
    position: relative;
    width: 56px;
    height: 56px;
    border-radius: 50%;
    background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
    display: flex;
    align-items: center;
    justify-content: center;
    color: white;
    font-size: 20px;
    cursor: pointer;
    box-shadow: 0 4px 20px rgba(0, 0, 0, 0.15);
    transition: all 0.3s cubic-bezier(0.25, 0.46, 0.45, 0.94);

    &:hover {
        transform: scale(1.1);
        box-shadow: 0 6px 25px rgba(0, 0, 0, 0.2);
    }

    &:active {
        transform: scale(0.95);
    }

    // When icon is shown with content
    &.widget-icon-with-content {
        position: absolute;
        z-index: 20;
        // Keep the same position to prevent shifting
        bottom: 0;
        right: 0;
    }

    // Icon states - overlay exactly on top of each other
    .widget-icon-show,
    .widget-icon-hide {
        position: absolute;
        top: 50%;
        left: 50%;
        transform: translate(-50%, -50%);
        transition: opacity 0.2s ease;
    }

    // Pulse animation for attention
    &::before {
        content: '';
        position: absolute;
        width: 100%;
        height: 100%;
        border-radius: 50%;
        background: inherit;
        animation: pulse 2s infinite;
        opacity: 0.7;
        z-index: -1;
    }
}

.widget-content {
    background: white;
    box-shadow: 0 8px 30px rgba(0, 0, 0, 0.12);
    overflow: hidden;
    animation: slideInBubble 0.4s cubic-bezier(0.25, 0.46, 0.45, 0.94);
    position: relative;

    &.widget-content-mobile {
        border-radius: 8px;
        margin: 0 10px;
    }
}

.btn-close-widget {
    position: absolute;
    top: -11px;
    right: -11px;
    width: 24px;
    height: 24px;
    background: white;
    border: 1px solid #ddd;
    border-radius: 50%;
    color: #666;
    font-size: 12px;
    cursor: pointer;
    display: flex;
    align-items: center;
    justify-content: center;
    transition: all 0.2s ease;
    z-index: 20;
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.15);

    &:hover {
        background: #f8f9fa;
        color: #333;
        transform: scale(1.1);
        box-shadow: 0 4px 12px rgba(0, 0, 0, 0.2);
    }

    &:active {
        transform: scale(0.95);
    }
}

// Position specific styles
/* .widget-position-bottom-right,
.widget-position-bottom-left,
.widget-position-bottom-center {
    .widget-content {
        margin-bottom: 70px; // Increased margin to make space for icon
    }
}

.widget-position-top-right,
.widget-position-top-left,
.widget-position-top-center {
    .widget-content {
        margin-top: 70px; // Increased margin to make space for icon
    }
} */

// Animations
@keyframes fadeInBubble {
    0% {
        opacity: 0;
        transform: scale(0.3);
    }
    50% {
        opacity: 0.8;
        transform: scale(1.05);
    }
    100% {
        opacity: 1;
        transform: scale(1);
    }
}

@keyframes slideInBubble {
    0% {
        opacity: 0;
        transform: scale(0.8) translateY(20px);
    }
    100% {
        opacity: 1;
        transform: scale(1) translateY(0);
    }
}

@keyframes pulse {
    0% {
        transform: scale(1);
        opacity: 0.7;
    }
    50% {
        transform: scale(1.1);
        opacity: 0.3;
    }
    100% {
        transform: scale(1.2);
        opacity: 0;
    }
}
</style>
